import type { DayPhase } from "./dayNightSchedule";

// The app's color theme: what the person chose, what that resolves to, and putting it on the page.
//
// The choice lives in localStorage (it must be readable before React and before sign-in, so the first
// paint is already right; index.html repeats the read in a small inline script). The resolved theme is
// written to <html data-theme>, the attribute both Chakra's `_dark` styles and
// src/theme/jungleThemeColors.css key on.

// "auto" is light by day and dark at night, by the sun where the person is (dayNightSchedule.ts). The
// device's own light or dark setting plays no part: automatic is Jungle's own (the owner, 2026-10-03).
export type JungleColorThemePreference = "auto" | "light" | "dark";
export type JungleColorTheme = "light" | "dark";

export const JUNGLE_COLOR_THEME_PREFERENCE_STORAGE_KEY = "jungle-color-theme-preference";
// Someone who never chose is on light during the day and dark at night; anything else is a choice they
// make (the owner, 2026-10-02).
export const DEFAULT_JUNGLE_COLOR_THEME_PREFERENCE: JungleColorThemePreference = "auto";

// The browser chrome around the page (mobile address bar, the installed app's title bar) follows this
// color. Each value is that theme's page background (`surface.primary`).
const PAGE_BACKGROUND_COLOR_BY_THEME: Record<JungleColorTheme, string> = {
  light: "#F5FCF8",
  dark: "#0B110E",
};

function isColorThemePreference(value: unknown): value is JungleColorThemePreference {
  // "system" (match device) was a choice for the first hours after launch: it now reads as no choice
  return value === "auto" || value === "light" || value === "dark";
}

// localStorage throws in some private windows and embedded browsers; the theme must never break the app.
function readFromLocalStorage(storageKey: string): string | null {
  try {
    return window.localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeToLocalStorage(storageKey: string, value: string): void {
  try {
    window.localStorage.setItem(storageKey, value);
  } catch {
    // The choice then lasts for this page load only.
  }
}

export function readStoredColorThemePreference(): JungleColorThemePreference {
  const storedPreference = readFromLocalStorage(JUNGLE_COLOR_THEME_PREFERENCE_STORAGE_KEY);
  return isColorThemePreference(storedPreference) ? storedPreference : DEFAULT_JUNGLE_COLOR_THEME_PREFERENCE;
}

export function storeColorThemePreference(colorThemePreference: JungleColorThemePreference): void {
  writeToLocalStorage(JUNGLE_COLOR_THEME_PREFERENCE_STORAGE_KEY, colorThemePreference);
}

// Which rule decides the theme of a page:
//   app         - the learner's product: follows the person's choice
//   adminTools  - the admin area keeps the light/dark switch it already had (AdminSidebarContent), stored
//                 under Chakra's own key; Jungle's dark colors never apply there
//   alwaysLight - the marketing pages (their own art direction) and internal pages
export type JungleColorThemeScope = "app" | "adminTools" | "alwaysLight";

// The mobile app shows the web app inside a native shell that draws the phone's status bar around the
// page (jungle-mobile, HomeScreen.tsx). An app version that says it can follow the page's theme
// (`colorThemeBridgeVersion` in the build info it injects before the page loads) is told the theme of
// every page (mobileAppColorThemeBridge.ts) and turns its status bar icons light over a dark page. Older
// versions draw dark icons on whatever color the page has: on a dark page the clock and battery would
// vanish, so inside them every page stays light. A page opened in a phone's browser is not affected.
export const MOBILE_APP_COLOR_THEME_BRIDGE_VERSION = 1;

export function isRunningInsideJungleMobileApp(): boolean {
  return typeof window !== "undefined" && Boolean((window as { ReactNativeWebView?: unknown }).ReactNativeWebView);
}

export function doesMobileAppFollowTheColorTheme(): boolean {
  if (typeof window === "undefined") return false;
  return window.__JUNGLE_NATIVE_APP_BUILD_INFO__?.colorThemeBridgeVersion === MOBILE_APP_COLOR_THEME_BRIDGE_VERSION;
}

/** False only inside a mobile app version that keeps every page light; the theme controls hide there. */
export function canThisDeviceShowTheDarkTheme(): boolean {
  return !isRunningInsideJungleMobileApp() || doesMobileAppFollowTheColorTheme();
}

function isAdminToolsPath(lowerCasePathname: string): boolean {
  // /quality is a public wrapper around an admin page
  return lowerCasePathname === "/admin" || lowerCasePathname.startsWith("/admin/") || lowerCasePathname === "/quality";
}

export function getColorThemeScopeForPath(pathname: string, isMarketingSitePath: boolean): JungleColorThemeScope {
  if (isMarketingSitePath || !canThisDeviceShowTheDarkTheme()) return "alwaysLight";
  const lowerCasePathname = pathname.toLowerCase();
  if (lowerCasePathname.startsWith("/internal/")) return "alwaysLight";
  if (isAdminToolsPath(lowerCasePathname)) return "adminTools";
  return "app";
}

/**
 * A page of the learner's product, whatever theme it shows: not the admin area, an internal tool or a
 * marketing page. Unlike the scope above it does not depend on the device: a mobile app version that keeps
 * every page light still shows learner pages, and they take the learner pages' focus styles and text colors
 * (src/theme/jungleThemeColors.css keys both on data-jungle-app).
 */
export function isLearnerPagePath(pathname: string, isMarketingSitePath: boolean): boolean {
  if (isMarketingSitePath) return false;
  const lowerCasePathname = pathname.toLowerCase();
  return !lowerCasePathname.startsWith("/internal/") && !isAdminToolsPath(lowerCasePathname);
}

// Chakra's default storage key: where the admin area's switch has always kept its choice.
export const ADMIN_TOOLS_COLOR_MODE_STORAGE_KEY = "chakra-ui-color-mode";

export function readStoredAdminToolsColorMode(): JungleColorTheme {
  return readFromLocalStorage(ADMIN_TOOLS_COLOR_MODE_STORAGE_KEY) === "dark" ? "dark" : "light";
}

export function storeAdminToolsColorMode(colorMode: JungleColorTheme): void {
  writeToLocalStorage(ADMIN_TOOLS_COLOR_MODE_STORAGE_KEY, colorMode);
}

export function resolveColorTheme({
  colorThemeScope,
  colorThemePreference,
  dayPhase,
  adminToolsColorMode,
}: {
  colorThemeScope: JungleColorThemeScope;
  colorThemePreference: JungleColorThemePreference;
  dayPhase: DayPhase;
  adminToolsColorMode: JungleColorTheme;
}): JungleColorTheme {
  if (colorThemeScope === "alwaysLight") return "light";
  if (colorThemeScope === "adminTools") return adminToolsColorMode;
  // Twilight is still the light page; only its scenes are painted at dusk
  if (colorThemePreference === "auto") return dayPhase === "night" ? "dark" : "light";
  return colorThemePreference;
}

/**
 * Whether the tree and river scenes are painted at dusk: the hour before the page turns dark and the hour
 * after it turns light again, for someone on the automatic appearance. A light or dark theme that was
 * chosen keeps its own scenes all day.
 */
export function isTwilightSceneShowing({
  colorThemeScope,
  colorThemePreference,
  dayPhase,
}: {
  colorThemeScope: JungleColorThemeScope;
  colorThemePreference: JungleColorThemePreference;
  dayPhase: DayPhase;
}): boolean {
  return colorThemeScope === "app" && colorThemePreference === "auto" && dayPhase === "twilight";
}

export function getPageBackgroundColorForTheme(colorTheme: JungleColorTheme): string {
  return PAGE_BACKGROUND_COLOR_BY_THEME[colorTheme];
}

// ── filters for pictures on a dark page ─────────────────────────────────────────────────────────────────
// Two things no CSS filter function can do, and an SVG filter can. A style rule can only point at a filter
// that is in the page (src/theme/jungleThemeArtwork.css), so the page carries this element while a dark
// theme is on.
//
// 1. Paper (the badge book, a tree's card, the examiner's sheet) is lit by a warm lamp: each color channel
//    is turned down by a different amount, which keeps cream cream where lowering the brightness turns it
//    grey.
//
// 2. A drawing made for a white page (a diagram) is inverted as a whole, and its hues turned back so a
//    colored segment keeps its color. invert() lands the drawing's white on a neutral grey: on a surface
//    that is not exactly that grey, every white patch behind a label shows as a rectangle, and beside
//    green-black surfaces the grey looks brown. This matrix does the same two steps and then adds the color
//    of the surface the drawing lies on, so white becomes that surface and black becomes a light text tint:
//    out = -contrast · hueRotate180 · in + contrast + surface.
const ARTWORK_FILTERS_ELEMENT_ID = "jungle-artwork-filters";
const LAMPLIGHT_FILTER_MARKUP =
  '<filter id="jungle-lamplight" color-interpolation-filters="sRGB">' +
  '<feColorMatrix type="matrix" values="0.90 0 0 0 0  0 0.86 0 0 0  0 0 0.78 0 0  0 0 0 1 0"/>' +
  "</filter>";

// How far the drawing's black ends up from its white. 0.75 puts labels between body and heading text.
const DRAWING_CONTRAST = 0.75;
// The matrix of the CSS filter hue-rotate(180deg)
const HUE_ROTATE_180_MATRIX = [
  [-0.574, 1.43, 0.144],
  [0.426, 0.43, 0.144],
  [0.426, 1.43, -0.856],
];
// The surfaces a drawing can lie on: a card or the chat panel (`surface.white`), the page-colored canvas of
// the expanded viewer (`surface.offWhite`), a raised card on a call (`surface.secondary`), and a floating box
// (the card color inside a dialog). Each value is that color in src/theme/jungleThemeColors.css;
// jungleColorTheme.test.ts fails when one drifts.
export const DRAWING_SURFACE_COLORS = { card: "#17221c", page: "#101713", raised: "#1f2d25", floating: "#24342b" };

function buildDrawingFilterMarkup(filterId: string, surfaceColor: string): string {
  const surfaceChannels = [1, 3, 5].map((start) => parseInt(surfaceColor.slice(start, start + 2), 16) / 255);
  const matrixRows = HUE_ROTATE_180_MATRIX.map((hueRotateRow, rowIndex) => {
    const colorPart = hueRotateRow.map((value) => (-DRAWING_CONTRAST * value).toFixed(3)).join(" ");
    const offset = (DRAWING_CONTRAST + surfaceChannels[rowIndex]).toFixed(3);
    return `${colorPart} 0 ${offset}`;
  });
  return (
    `<filter id="${filterId}" color-interpolation-filters="sRGB">` +
    `<feColorMatrix type="matrix" values="${matrixRows.join("  ")}  0 0 0 1 0"/>` +
    "</filter>"
  );
}

function buildArtworkFiltersMarkup(): string {
  return (
    `<svg id="${ARTWORK_FILTERS_ELEMENT_ID}" width="0" height="0" aria-hidden="true" focusable="false" style="position:absolute">` +
    LAMPLIGHT_FILTER_MARKUP +
    buildDrawingFilterMarkup("jungle-drawing-on-card", DRAWING_SURFACE_COLORS.card) +
    buildDrawingFilterMarkup("jungle-drawing-on-page", DRAWING_SURFACE_COLORS.page) +
    buildDrawingFilterMarkup("jungle-drawing-on-raised", DRAWING_SURFACE_COLORS.raised) +
    buildDrawingFilterMarkup("jungle-drawing-on-floating", DRAWING_SURFACE_COLORS.floating) +
    "</svg>"
  );
}

function keepArtworkFiltersInDocument(shouldBeInDocument: boolean): void {
  const artworkFiltersElement = document.getElementById(ARTWORK_FILTERS_ELEMENT_ID);
  if (!shouldBeInDocument) {
    artworkFiltersElement?.remove();
    return;
  }
  if (artworkFiltersElement) return;
  // Without a <body> yet there is nothing on the page to light either; the next call adds it.
  document.body?.insertAdjacentHTML("beforeend", buildArtworkFiltersMarkup());
}

// ── the keyboard ring ───────────────────────────────────────────────────────────────────────────────────
// A learner's page draws a ring around the control that has keyboard focus, in both themes. A dialog that
// opens by itself moves focus to its first control by script, and the browser counts that as keyboard
// focus: the ring would land on a choice nobody made. So the ring also needs this attribute on <html>,
// which is there from a press of the Tab key until the next press of a pointer
// (src/theme/jungleThemeColors.css, "keyboard focus").
export const KEYBOARD_IN_USE_ATTRIBUTE = "data-jungle-keyboard";

/** Starts keeping KEYBOARD_IN_USE_ATTRIBUTE up to date; returns the function that stops it. */
export function markDocumentWhileKeyboardIsInUse(): () => void {
  const markKeyboardInUse = (keyboardEvent: KeyboardEvent) => {
    if (keyboardEvent.key === "Tab") document.documentElement.setAttribute(KEYBOARD_IN_USE_ATTRIBUTE, "");
  };
  const markPointerInUse = () => document.documentElement.removeAttribute(KEYBOARD_IN_USE_ATTRIBUTE);
  // In the capture phase: a component that stops the event from spreading must not hide it from here.
  document.addEventListener("keydown", markKeyboardInUse, true);
  document.addEventListener("pointerdown", markPointerInUse, true);
  return () => {
    document.removeEventListener("keydown", markKeyboardInUse, true);
    document.removeEventListener("pointerdown", markPointerInUse, true);
    document.documentElement.removeAttribute(KEYBOARD_IN_USE_ATTRIBUTE);
  };
}

// Chakra's color mode sets data-theme and the color-scheme style too, but only once React has rendered.
// Doing it here as well keeps the page right when this runs first, and is harmless when Chakra agrees.
// Jungle's dark colors are keyed on the data-jungle-dark attribute, which only an app page gets: a dark
// admin page keeps the light Jungle colors it was built with.
// data-jungle-twilight marks the hour in which the scenes are painted at dusk on the light page
// (isTwilightSceneShowing; the rules are in src/theme/jungleThemeArtwork.css).
// data-jungle-app marks a learner's page in either theme (isLearnerPagePath). The keyboard ring, the
// focused field's green line and a few light text colors key on it, so the admin area and the marketing
// pages keep the focus styles and colors they were built with.
export function applyColorThemeToDocument({
  colorTheme,
  colorThemeScope,
  isTwilightScene = false,
  isLearnerPage = colorThemeScope === "app",
}: {
  colorTheme: JungleColorTheme;
  colorThemeScope: JungleColorThemeScope;
  isTwilightScene?: boolean;
  isLearnerPage?: boolean;
}): void {
  const rootElement = document.documentElement;
  const isAppPageInDarkTheme = colorTheme === "dark" && colorThemeScope === "app";
  rootElement.dataset.theme = colorTheme;
  rootElement.style.colorScheme = colorTheme;
  if (isLearnerPage) {
    rootElement.dataset.jungleApp = "";
  } else {
    delete rootElement.dataset.jungleApp;
  }
  if (isAppPageInDarkTheme) {
    rootElement.dataset.jungleDark = "";
  } else {
    delete rootElement.dataset.jungleDark;
  }
  if (isTwilightScene && !isAppPageInDarkTheme) {
    rootElement.dataset.jungleTwilight = "";
  } else {
    delete rootElement.dataset.jungleTwilight;
  }
  keepArtworkFiltersInDocument(isAppPageInDarkTheme);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", getPageBackgroundColorForTheme(isAppPageInDarkTheme ? "dark" : "light"));
}
